import { Injectable, inject } from "@angular/core";
import type { NiceAsyncTypeaheadSearchResult } from "@recursyve/nice-ui-kit.v2";
import { NiceAsyncTypeaheadProvider } from "@recursyve/nice-ui-kit.v2";
import { deburr } from "lodash-es";
import type { Observable } from "rxjs";
import { map } from "rxjs/operators";

import type { PropertyStreets } from "../../models/property-streets.model";
import { PropertiesApi } from "../properties.api";

@Injectable()
export class PropertyStreetsTypeaheadApi extends NiceAsyncTypeaheadProvider<PropertyStreets, string> {
    private readonly api = inject(PropertiesApi);

    private readonly pageSize = 20;
    public readonly resource = "propertyStreets";

    public override async getById(id: string): Promise<PropertyStreets> {
        return Promise.resolve({ id, name: id });
    }

    public override search(
        searchQuery: string,
        page: number
    ): Observable<NiceAsyncTypeaheadSearchResult<PropertyStreets>> {
        return this.api.searchStreets({ page, pageSize: this.pageSize, searchQuery }).pipe(
            map((items) => ({
                items: page === 0 ? this.withTypedStreet(items, searchQuery) : items,
                nextPage: items.length === this.pageSize ? page + 1 : null
            }))
        );
    }

    public override format(street: PropertyStreets): string {
        return street.name;
    }

    private withTypedStreet(items: PropertyStreets[], searchQuery: string): PropertyStreets[] {
        const typedStreet = searchQuery.trim();
        if (!typedStreet || items.some((item) => this.isSameStreet(item.name, typedStreet))) {
            return items;
        }

        return [{ id: typedStreet, name: typedStreet, isTyped: true }, ...items];
    }

    private isSameStreet(a: string, b: string): boolean {
        return deburr(a).toLowerCase() === deburr(b).toLowerCase();
    }
}
